import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="bg-gradient-to-br from-primary to-primary-hover p-2 rounded-lg shadow-lg">
              <Package className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <span className="font-manrope font-bold text-xl text-text-primary tracking-tight">Keeboxs</span>
          </button>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-text-muted hover:text-text-primary transition-colors">
            <ArrowLeft size={16} /> Back
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-16" data-testid="privacy-policy-page">
        <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-text-muted text-sm mb-10">Last updated: 09 July 2026</p>

        <div className="prose prose-gray max-w-none space-y-8 text-text-secondary text-sm leading-relaxed">
          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">1. Introduction</h2>
            <p>Keeboxs ("we", "our", or "us"), operated by Sysqua Solutions Private Limited, is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our credential and certificate management platform.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">2. Information We Collect</h2>
            <p className="mb-2"><strong className="text-text-primary">Account Information:</strong> When you create an account, we collect your name, email address, organization name, and encrypted password.</p>
            <p className="mb-2"><strong className="text-text-primary">Credential Data:</strong> All credentials and certificates you store are encrypted using AES-256 encryption with organization-specific keys. We cannot access your decrypted credential data.</p>
            <p className="mb-2"><strong className="text-text-primary">Usage Data:</strong> We collect audit logs of actions performed within the platform, including logins, credential access, and sharing activities for security and compliance purposes.</p>
            <p><strong className="text-text-primary">File Uploads:</strong> Files attached to credentials (PEM, PPK, certificates) are stored securely using GridFS with strict file type and size validation.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To provide, operate, and maintain the Keeboxs platform</li>
              <li>To authenticate users and enforce role-based access control</li>
              <li>To enable secure credential and certificate sharing within your organization</li>
              <li>To generate audit trails and compliance reports</li>
              <li>To send transactional emails (account creation, security alerts)</li>
              <li>To enforce subscription plan limits and feature access</li>
              <li>To improve and optimize the platform</li>
            </ul>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">4. Data Security</h2>
            <p>We implement industry-standard security measures including:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>AES-256 Fernet encryption for all stored credentials</li>
              <li>TOTP-based two-factor authentication (2FA)</li>
              <li>Brute-force protection with account lockout policies</li>
              <li>Rate limiting on all sensitive API endpoints</li>
              <li>HTTPS with security headers (HSTS, CSP, X-Frame-Options)</li>
              <li>Organization-level data isolation</li>
            </ul>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">5. Data Sharing & Disclosure</h2>
            <p>We do not sell, trade, or otherwise transfer your information to outside parties. Your credential data is shared only with team members within your organization as explicitly authorized by you. We may disclose information if required by law or to protect our rights.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">6. Data Retention</h2>
            <p>We retain your account data and encrypted credentials for as long as your account is active. Audit logs are retained for compliance purposes. Upon account deletion, all associated data is permanently removed within 30 days.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">7. Cookies</h2>
            <p>We use essential cookies for authentication (JWT tokens stored in local storage) and session management. We do not use third-party tracking or advertising cookies. You can manage your cookie preferences at any time.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">8. Your Rights</h2>
            <p>You have the right to access, correct, or delete your personal data. You may export your credentials, manage sharing permissions, and request account deletion by contacting our support team.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">9. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us at:</p>
            <p className="mt-2"><strong className="text-text-primary">Email:</strong> support@keeboxs.com | support@sysqua.com</p>
            <p><strong className="text-text-primary">Website:</strong> <a href="https://sysqua.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">sysqua.com</a></p>
          </section>
        </div>
      </div>
    </div>
  );
}
